import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
	Bot,
	Brain,
	Calendar,
	CheckCircle,
	Key,
	Loader2,
	Mail,
	Receipt,
	RefreshCw,
	XCircle,
} from "lucide-react";
import { useState } from "react";
import type { IntegrationStatus } from "./settings-types";
import { StatusBadge } from "./settings-ui";

// ============================================================
// Integration Grid — Status + Test Buttons
// ============================================================

const SERVICE_ICONS: Record<string, typeof Mail> = {
	gmail: Mail,
	lexoffice: Receipt,
	telegram: Bot,
	anthropic: Brain,
	calendar: Calendar,
};

export function IntegrationGrid() {
	const query = useQuery({
		queryKey: ["settings-status"],
		queryFn: () => api.get<{ data: IntegrationStatus }>("/settings/status"),
	});

	const [testResults, setTestResults] = useState<
		Record<string, { ok: boolean; message: string } | "loading">
	>({});

	async function testService(service: string) {
		setTestResults((prev) => ({ ...prev, [service]: "loading" }));
		try {
			const res = await api.post<{
				data: { ok: boolean; message: string };
			}>(`/settings/test/${service}`);
			setTestResults((prev) => ({ ...prev, [service]: res.data }));
		} catch {
			setTestResults((prev) => ({
				...prev,
				[service]: { ok: false, message: "Fehler beim Testen" },
			}));
		}
	}

	const integrations = query.data?.data ?? {};

	if (query.isLoading) {
		return (
			<div className="flex justify-center py-8">
				<Loader2 className="h-6 w-6 animate-spin text-stone-400" />
			</div>
		);
	}

	return (
		<div className="space-y-3">
			<h2 className="font-medium text-stone-700">Integrationen</h2>
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
				{Object.entries(integrations).map(([key, info]) => {
					const Icon = SERVICE_ICONS[key] ?? Key;
					const test = testResults[key];
					const isLoading = test === "loading";
					const result = test && test !== "loading" ? test : null;

					return (
						<div key={key} className="card space-y-3">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-2">
									<Icon size={18} className="text-stone-500" />
									<span className="font-medium text-stone-800">
										{info.label}
									</span>
								</div>
								<StatusBadge configured={info.configured} />
							</div>

							<div className="space-y-1 text-xs text-stone-500">
								{Object.entries(info.fields).map(([field, val]) => (
									<div key={field} className="flex justify-between">
										<span>{field}</span>
										<span
											className={cn(
												typeof val === "boolean"
													? val
														? "text-green-600"
														: "text-stone-300"
													: "text-stone-600",
											)}
										>
											{typeof val === "boolean"
												? val
													? "OK"
													: "fehlt"
												: String(val)}
										</span>
									</div>
								))}
							</div>

							{result && (
								<div
									className={cn(
										"text-xs px-2 py-1.5 rounded-md",
										result.ok
											? "bg-green-50 text-green-700"
											: "bg-red-50 text-red-700",
									)}
								>
									{result.ok ? (
										<CheckCircle size={12} className="inline mr-1" />
									) : (
										<XCircle size={12} className="inline mr-1" />
									)}
									{result.message}
								</div>
							)}

							<button
								type="button"
								disabled={isLoading || !info.configured}
								onClick={() => testService(key)}
								className="btn-ghost text-xs w-full disabled:opacity-40"
							>
								{isLoading ? (
									<Loader2 size={12} className="animate-spin inline mr-1" />
								) : (
									<RefreshCw size={12} className="inline mr-1" />
								)}
								Verbindung testen
							</button>
						</div>
					);
				})}
			</div>
		</div>
	);
}
